import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import { Box, alpha } from '@mui/material'

import { space } from 'settings/tokens'
import { PAGE_SLIDE_MS } from 'utils/pageTransition'
import { glassSx, glassEdge } from 'utils/glass'

/** Height of the bar's CONTENT row — one 48px control. */
export const ACTION_BAR_HEIGHT = 48

/** Padding above and below that row. */
export const ACTION_BAR_PAD = space.lg

/** Total space the bar occupies, for a page to reserve at the end of its flow. */
export const ACTION_BAR_RESERVED = ACTION_BAR_HEIGHT + ACTION_BAR_PAD * 2

/** Content column cap — see the note on width in `StickyActionBar`. */
const ACTION_BAR_MAX_WIDTH = 480

interface ActionBarContextValue {
  /** The element portalled actions render into. Null until the bar mounts. */
  slot: HTMLElement | null
  setSlot: (el: HTMLElement | null) => void
  /** How many actions are currently mounted. 0 means the bar renders nothing. */
  count: number
  /** Called by a portalled action on mount; returns its unregister. */
  register: () => () => void
}

const ActionBarContext = createContext<ActionBarContextValue | null>(null)

/**
 * A bottom action bar that is present only when the surface has an action.
 *
 * ## Why a slot rather than a prop
 *
 * The certificate page's actions are not the page's to render. `Cancel gift`,
 * `Claim ownership` and `Deny gift` each live inside a transfer section that
 * also owns the mutation, the scan-tag drawer and the success drawer that the
 * button drives. Lifting the LABEL into the page would mean lifting all of
 * that with it, or splitting each action across two files.
 *
 * So the bar publishes a slot and the owning component renders its button into
 * it. The action keeps its logic where it already is; only its pixels move.
 *
 * ## Why it counts registrations
 *
 * The bar must vanish — not sit empty — when the certificate has no pending
 * action, which is the common case for something you simply own (the decision
 * on 2026-08-12: no bar when idle, rather than a permanent CTA for an action
 * taken twice in an object's life). A portal target cannot be measured for
 * emptiness without an observer, so each `ActionBarPortal` registers itself and
 * the bar hides at zero.
 */
export const ActionBarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  const [count, setCount] = useState(0)

  const register = useCallback(() => {
    setCount(c => c + 1)
    return () => setCount(c => c - 1)
  }, [])

  const value = useMemo(
    () => ({ slot, setSlot, count, register }),
    [slot, count, register],
  )

  return <ActionBarContext.Provider value={value}>{children}</ActionBarContext.Provider>
}

/** Null outside a provider — callers must tolerate that (see `ActionBarPortal`). */
export const useActionBar = (): ActionBarContextValue | null => useContext(ActionBarContext)

/** Whether the surface currently has any pinned action. */
export const useHasActionBar = (): boolean => (useActionBar()?.count ?? 0) > 0

/**
 * Renders its children into the sticky bar.
 *
 * Falls back to rendering INLINE when there is no provider above it, so a
 * section that is reused on a page without a bar still shows its action rather
 * than silently losing it.
 */
export const ActionBarPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const ctx = useActionBar()
  const register = ctx?.register

  useEffect(() => {
    if (!register) return
    return register()
  }, [register])

  if (!ctx) return <>{children}</>
  if (!ctx.slot) return null

  return createPortal(children, ctx.slot)
}

/**
 * Reserves the bar's space at the end of the page's flow, so the last row is
 * not sitting underneath it. Renders nothing when there is no bar — which is
 * the whole reason this is a component and not a constant padding.
 */
export const ActionBarSpacer: React.FC = () => {
  const hasAction = useHasActionBar()
  if (!hasAction) return null
  return (
    <Box
      aria-hidden
      sx={{ height: `calc(${ACTION_BAR_RESERVED}px + env(safe-area-inset-bottom, 0px))` }}
    />
  )
}

/**
 * The bar itself. Render once, near the end of the page, inside the provider.
 *
 * ## Material
 *
 * The same glass as `BottomNav` — translucent fill, blur PLUS saturation (blur
 * alone greys out what is behind it and reads as frosted plastic), `-webkit-`
 * prefixed because every device this app is opened on is an iPhone.
 *
 * ## Edge-to-edge, unlike the nav
 *
 * `BottomNav` floats inset on three sides because it is chrome you switch
 * between. This is the page's one commercial action: both reference apps
 * (Chrono24's Buy, Artsy's Make an Offer) run it full width against the bottom
 * edge, which also gives the largest possible target at the point of the phone
 * the thumb reaches best.
 *
 * The CONTENT is capped at 480px and centred so the same bar is not a
 * 1280px-wide button on a desktop viewport, where this page is a mobile-width
 * column in the middle of the screen.
 *
 * The safe-area inset is added to PADDING, not height: the control stays 48px
 * and only the background extends into the home-indicator region.
 */
export const StickyActionBar: React.FC = () => {
  const ctx = useActionBar()
  const hasAction = (ctx?.count ?? 0) > 0

  return (
    <Box
      // The slot element must exist for portalled actions to mount into, so the
      // bar is always in the DOM and hidden by `display` — not unmounted — when
      // empty. Unmounting it would tear down the target that the very action
      // trying to register is portalling into.
      sx={theme => ({
        display: hasAction ? 'block' : 'none',
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        // Page chrome, below MUI's drawer level (1200) so every bottom sheet in
        // this app — including the scan-tag drawer these actions open — is
        // above it rather than fighting it on the same layer.
        zIndex: 1100,
        px: `${ACTION_BAR_PAD}px`,
        pt: `${ACTION_BAR_PAD}px`,
        pb: `calc(${ACTION_BAR_PAD}px + env(safe-area-inset-bottom, 0px))`,

        ...glassSx(theme),
        // `!important`: `#vera-shadow-root * { border-color: … }` in index.css
        // has ID specificity and silently discards an sx border colour without
        // it. Property form is irrelevant — see settings/tokens.ts.
        borderTop: glassEdge(theme),


        /*
         * RISES into place, every time, for every consumer.
         *
         * Some of these bars appeared to animate up already and others hard-cut
         * in, which is what made the behaviour feel arbitrary. Neither was
         * deliberate: this component had no animation at all, so a bar only
         * looked like it rose when the whole PAGE happened to be sliding in
         * underneath it and carried the bar along. A bar that appears later —
         * once its data lands, or when an action registers — got nothing.
         *
         * An ANIMATION, not a transition, because the bar is shown and hidden
         * with `display` (it must stay mounted as the portal target — see the
         * note above). `display` is not an animatable property, so a transition
         * on transform never runs: the element goes straight from not-rendered
         * to rendered at its final position. A keyframe animation, by contrast,
         * starts fresh each time the element becomes displayed, which is exactly
         * the trigger wanted here and needs no extra state to drive it.
         *
         * Rises from 100% of its OWN height, so it starts fully below the bottom
         * edge and slides up into place rather than fading in already half-shown.
         */
        animation: `veraActionBarRise ${PAGE_SLIDE_MS}ms cubic-bezier(0.32, 0.72, 0, 1) both`,
        '@keyframes veraActionBarRise': {
          from: { transform: 'translateY(100%)' },
          to: { transform: 'translateY(0)' },
        },
        /*
         * A bar that slides up the screen is exactly the motion that triggers
         * vestibular symptoms, and it is pinned where the eye already is. Honour
         * the OS setting: no movement, and no fade substituted for it either —
         * the bar simply is there.
         */
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      })}
    >
      <Box
        ref={(el: HTMLDivElement | null) => ctx?.setSlot(el)}
        sx={{
          maxWidth: ACTION_BAR_MAX_WIDTH,
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: `${space.md}px`,
          '& > *': { flex: 1, minWidth: 0 },
        }}
      />
    </Box>
  )
}
